import { useEffect, useState } from 'react';
import NextLink from 'next/link';
import axiosInstance from '../../utils/axios';
import { NOVIDADES_VERSION } from '../../sections/dashboard/novidades/novidadesData';
import { PATH_DASHBOARD } from '../../routes/paths';

/**
 * Rodape do dashboard - padrao visual novo (shadcn + Tailwind, tokens --gps-*).
 *
 * Mudancas em relacao a versao anterior:
 *
 * 1. De TRES itens soltos para DOIS CARTOES. "Novidades" foi absorvido por
 *    Documentos ("Manual e novidades"): eram dois destinos de leitura, e separa-los
 *    obrigava o usuario a adivinhar em qual dos dois estava o que ele procura.
 *
 * 2. Cada cartao carrega um SELO com informacao viva, nao enfeite:
 *      Suporte    -> quantidade de tickets em aberto (vem de /tickets/contadores)
 *      Documentos -> versao do sistema (vem de novidadesData, nao e string solta)
 *    Selo que nao informa nada e ruido; estes dois respondem "tenho pendencia?" e
 *    "que versao estou usando?" sem exigir clique.
 *
 * 4. OS DOIS CARTOES APARECEM EM QUALQUER ALTURA DE TELA. Em 26/08/2026 eu os
 *    escondi em tela baixa para ganhar altura na lista, e foi errado: Suporte e
 *    Documentos sao o caminho de quem precisa de ajuda, e tirar exatamente na tela
 *    menor deixa sem saida quem esta com problema. Altura se ganha em espacamento,
 *    nao removendo a porta de saida.
 *
 * 3. O contador falha em SILENCIO de proposito: se a chamada nao responder, o selo
 *    some e o cartao continua funcionando. Rodape nao pode mostrar erro nem prender
 *    a navegacao por causa de um numero acessorio.
 */
export default function RodapeDashboard() {
  const [ticketsAbertos, setTicketsAbertos] = useState<number | null>(null);

  useEffect(() => {
    let ativo = true;
    axiosInstance
      .get<any>('tickets/contadores')
      .then((r) => {
        if (!ativo) return;
        const d = r.data || {};
        const n = Number(d.abertos ?? d.aberto ?? d.total);
        setTicketsAbertos(Number.isFinite(n) && n > 0 ? n : null);
      })
      .catch(() => {
        /* selo apenas some */
      });
    return () => {
      ativo = false;
    };
  }, []);

  const itens = [
    {
      titulo: 'Suporte',
      descricao: 'Abrir e acompanhar tickets',
      caminho: PATH_DASHBOARD.general.tickets,
      selo: ticketsAbertos !== null ? String(ticketsAbertos) : null,
      // Verde: acao de atendimento. O icone fica em quadrado tingido, nao solto.
      corIcone: 'bg-ok-soft text-ok',
      icone: '🎧',
    },
    {
      titulo: 'Documentos',
      descricao: 'Manual e novidades',
      caminho: PATH_DASHBOARD.general.manual,
      selo: NOVIDADES_VERSION,
      corIcone: 'bg-info-soft text-info',
      icone: '📄',
    },
  ];

  return (
    <footer className="mt-auto w-full border-t border-line bg-surface-2">
      <div className="mx-auto flex w-full max-w-[1560px] flex-col items-center gap-2 px-4 py-2 sm:flex-row sm:justify-between lg:px-6">
        <span className="text-[12px] text-faint">
          © {new Date().getFullYear()} Grupo GPS · GPShub
        </span>

        <div className="flex flex-wrap items-center justify-center gap-2">
          {itens.map((item) => (
            <NextLink key={item.titulo} href={item.caminho} passHref>
              <span className="group inline-flex cursor-pointer items-center gap-2.5 rounded-lg border border-line bg-surface px-3 py-2 no-underline transition-colors hover:border-primary">
                <span className={`grid h-8 w-8 flex-none place-items-center rounded-lg text-[14px] ${item.corIcone}`}>
                  {item.icone}
                </span>

                <span className="min-w-0">
                  <span className="block text-[12.5px] font-bold leading-tight text-ink">{item.titulo}</span>
                  <span className="block text-[11px] leading-tight text-muted-foreground">{item.descricao}</span>
                </span>

                {item.selo && (
                  <span className="ml-1 rounded-md bg-soft px-1.5 py-0.5 text-[11px] tabular-nums text-muted-foreground">
                    {item.selo}
                  </span>
                )}

                <span className="text-faint transition-colors group-hover:text-primary">›</span>
              </span>
            </NextLink>
          ))}
        </div>
      </div>
    </footer>
  );
}
